import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, Filter, Send, Video, Phone, MoreVertical, Bot, User, Power, Search, Star, Archive, Trash2, Clock, CheckCheck, Paperclip, Smile, Mic, Image as ImageIcon, TrendingUp, Users, Zap, AlertCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import conversationService from '../../api/services/conversationService';

const normalizeContent = (m) => ({
  ...m,
  content: typeof m.content === 'object' && m.content !== null
    ? (m.content.content ?? m.content.text ?? m.content.body ?? JSON.stringify(m.content))
    : m.content,
});

export default function ConversationsConnected() {
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [aiEnabled, setAiEnabled] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sending, setSending] = useState(false);
  const initialSelectionDone = useRef(false);

  // ── Conversations ─────────────────────────────────────────────────────────
  const { data: convsData, isLoading, isError, error, refetch: refetchConvs, isFetching } = useQuery({
    queryKey: ['conversations', 'all'],
    queryFn: () => conversationService.getConversations({}),
  });

  const conversations = (convsData?.success && Array.isArray(convsData.data))
    ? convsData.data
    : [];

  // Sélection initiale : URL param ou première conversation
  useEffect(() => {
    if (!conversations.length || initialSelectionDone.current) return;
    const targetId = searchParams.get('id');
    if (targetId) {
      const target = conversations.find((c) => c._id === targetId);
      if (target) { setSelectedConversation(target); initialSelectionDone.current = true; }
    } else {
      setSelectedConversation(conversations[0]);
      initialSelectionDone.current = true;
    }
  }, [conversations, searchParams]);

  // Ré-ouvre la conv ciblée si l'URL change après le premier mount
  useEffect(() => {
    const targetId = searchParams.get('id');
    if (!targetId) return;
    const target = conversations.find((c) => c._id === targetId);
    if (target) setSelectedConversation(target);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Messages ──────────────────────────────────────────────────────────────
  const convId = selectedConversation?._id ?? null;
  const { data: messagesData, isLoading: loadingMessages } = useQuery({
    queryKey: ['messages', convId],
    queryFn: () => conversationService.getMessages(convId),
    enabled: !!convId,
  });

  const messages = (messagesData?.success && Array.isArray(messagesData.data))
    ? messagesData.data.map(normalizeContent)
    : [];

  // ── Envoi de message ──────────────────────────────────────────────────────
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sending) return;
    try {
      setSending(true);
      const response = await conversationService.sendMessage(selectedConversation._id, {
        content: newMessage,
        contentType: 'text',
      });
      if (response.success) {
        setNewMessage('');
        qc.invalidateQueries({ queryKey: ['messages', selectedConversation._id] });
      }
      setSending(false);
    } catch (err) {
      console.error('❌ Erreur envoi message:', err);
      alert('Erreur lors de l\'envoi du message');
      setSending(false);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
  };

  const stats = {
    total: conversations.length,
    unread: conversations.filter(c => c.status === 'active').length,
    treated: conversations.filter(c => c.status === 'closed').length,
    pending: conversations.filter(c => c.status === 'pending').length,
  };

  const filteredConversations = conversations.filter(conv => {
    if (filterStatus === 'unread' && conv.status !== 'active') return false;
    if (filterStatus === 'treated' && conv.status !== 'closed') return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        conv.clientName?.toLowerCase().includes(query) ||
        conv.clientEmail?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex overflow-hidden -m-8 h-screen items-center justify-center" style={{ background: 'var(--c-primary-dark)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4"></div>
          <p className="text-text-gray">Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  if (isError && !convsData) {
    return (
      <div className="flex overflow-hidden -m-8 h-screen items-center justify-center" style={{ background: 'var(--c-primary-dark)' }}>
        <div className="bg-red-500/10 border border-red-500 rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <h3 className="text-red-500 font-semibold">Erreur</h3>
          </div>
          <p className="text-text-gray mb-4">{error?.message}</p>
          <button
            onClick={() => refetchConvs()}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden -m-8 h-screen" style={{ background: 'var(--c-primary-dark)' }}>
        {/* Liste des conversations - Sidebar gauche */}
        <div className="w-80 flex flex-col flex-shrink-0" style={{ background: 'var(--c-primary-card)', borderRight: '0.5px solid var(--c-border)' }}>
          {/* Header avec stats */}
          <div className="p-4" style={{ borderBottom: '0.5px solid var(--c-border)' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-ink">Conversations</h2>
              <button
                onClick={() => refetchConvs()}
                className="p-2 hover:bg-[var(--c-hover)] rounded-lg transition-all"
              >
                <RefreshCw className={`text-label hover:text-accent-green ${isFetching ? 'animate-spin' : ''}`} size={16} />
              </button>
            </div>
            
            {/* Stats rapides */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="rounded-lg p-2 text-center cursor-pointer"
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              >
                <p className="text-xl font-bold text-ink">{stats.total}</p>
                <p className="text-xs text-label">Total</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-blue-500/10 rounded-lg p-2 text-center cursor-pointer border border-blue-500/20"
              >
                <p className="text-xl font-bold text-blue-400">{stats.unread}</p>
                <p className="text-xs text-blue-400">Actives</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-accent-green/10 rounded-lg p-2 text-center cursor-pointer border border-accent-green/20"
              >
                <p className="text-xl font-bold text-accent-green">{stats.treated}</p>
                <p className="text-xs text-accent-green">Fermées</p>
              </motion.div>
            </div>
            
            {/* Barre de recherche */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-label" size={15} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-ink text-sm pl-9 pr-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-accent-green/40 transition-all"
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              />
            </div>
            
            {/* Filtres */}
            <div className="flex gap-1.5">
              {[
                { id: 'all', label: 'Tous', icon: MessageCircle },
                { id: 'unread', label: 'Actives', icon: Clock },
                { id: 'treated', label: 'Fermées', icon: CheckCheck }
              ].map((filter) => (
                <motion.button
                  key={filter.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setFilterStatus(filter.id)}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                    filterStatus === filter.id
                      ? 'bg-accent-green text-black'
                      : 'text-text-gray hover:text-ink hover:bg-[var(--c-hover)]'
                  }`}
                >
                  <filter.icon size={12} />
                  <span>{filter.label}</span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Liste des conversations */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageCircle className="w-12 h-12 text-text-gray mx-auto mb-3" />
                <p className="text-text-gray text-sm">Aucune conversation</p>
              </div>
            ) : (
              filteredConversations.map((conv, index) => (
                <motion.div
                  key={conv._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleSelectConversation(conv)}
                  className="p-3 cursor-pointer transition-all relative"
                  style={{
                    borderBottom: '0.5px solid var(--c-border)',
                    background: selectedConversation?._id === conv._id ? 'var(--c-primary-dark)' : 'transparent',
                    borderLeft: selectedConversation?._id === conv._id ? '2px solid rgb(var(--c-accent-green-rgb))' : '2px solid transparent',
                  }}
                  onMouseEnter={(e) => { if (selectedConversation?._id !== conv._id) e.currentTarget.style.background = 'var(--c-hover)'; }}
                  onMouseLeave={(e) => { if (selectedConversation?._id !== conv._id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-black text-xs"
                        style={{ background: 'rgb(var(--c-accent-green-rgb))' }}
                      >
                        {(conv.clientName || 'U').substring(0, 2).toUpperCase()}
                      </motion.div>
                      {conv.status === 'active' && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-primary-card"></div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <h3 className="text-ink font-semibold text-sm truncate pr-2">
                          {conv.clientName || conv.clientEmail || 'Client'}
                        </h3>
                        <span className="text-xs text-label flex-shrink-0">
                          {new Date(conv.lastMessageAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          conv.status === 'active' ? 'bg-green-500/20 text-green-500' :
                          conv.status === 'closed' ? 'bg-gray-500/20 text-text-gray' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {conv.status === 'active' ? 'Active' : conv.status === 'closed' ? 'Fermée' : 'En attente'}
                        </span>
                        <span className="text-xs text-label capitalize">{conv.channel}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* Zone de chat */}
        {selectedConversation ? (
          <div className="flex-1 flex flex-col" style={{ background: 'var(--c-primary-dark)' }}>
            {/* En-tête de la conversation */}
            <div className="px-6 py-3" style={{ borderBottom: '0.5px solid var(--c-border)', background: 'var(--c-primary-card)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-black text-sm" style={{ background: 'rgb(var(--c-accent-green-rgb))' }}>
                    {(selectedConversation.clientName || 'U').substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-ink font-semibold text-base">
                      {selectedConversation.clientName || selectedConversation.clientEmail || 'Client'}
                    </h3>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        selectedConversation.status === 'active' ? 'bg-green-500' : 'bg-gray-500'
                      }`}></div>
                      <span className="text-xs text-label capitalize">
                        {selectedConversation.status === 'active' ? 'En ligne' : 'Hors ligne'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Toggle IA */}
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                    <span className="text-xs text-label font-medium">Agent IA</span>
                    <button
                      onClick={() => setAiEnabled(!aiEnabled)}
                      className={`relative w-11 h-6 rounded-full transition-all duration-300 ${
                        aiEnabled ? 'bg-accent-green' : 'bg-gray-600'
                      }`}
                    >
                      <motion.div
                        animate={{ x: aiEnabled ? 20 : 2 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg"
                      />
                    </button>
                    <Bot className={`${aiEnabled ? 'text-accent-green' : 'text-text-gray'} transition-colors`} size={16} />
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-8 space-y-5" style={{ background: 'var(--c-primary-dark)' }}>
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-green"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <MessageCircle className="w-12 h-12 text-text-gray mx-auto mb-3" />
                    <p className="text-text-gray">Aucun message</p>
                  </div>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <motion.div
                    key={msg._id || msg.id}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ 
                      delay: index * 0.08,
                      type: "spring",
                      stiffness: 200,
                      damping: 20
                    }}
                    whileHover={{ scale: 1.01 }}
                    className={`flex items-start gap-3 ${msg.senderType === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <motion.div 
                      whileHover={{ scale: 1.15, rotate: 5 }}
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={msg.senderType === 'agent' || msg.senderType === 'system'
                        ? { background: 'rgb(var(--c-accent-green-rgb))', color: '#000' }
                        : { background: '#3b82f6', color: '#fff' }
                      }
                    >
                      {msg.senderType === 'agent' || msg.senderType === 'system' ? <Bot size={18} /> : <User size={18} />}
                    </motion.div>
                    <div className={`flex flex-col ${msg.senderType === 'user' ? 'items-end' : 'items-start'} max-w-[70%]`}>
                      <motion.div 
                        whileHover={{ y: -2 }}
                        className={`px-5 py-3.5 rounded-2xl ${
                          msg.senderType === 'agent' || msg.senderType === 'system'
                            ? 'text-ink rounded-tl-sm'
                            : 'text-white rounded-tr-sm'
                        }`}
                        style={msg.senderType === 'agent' || msg.senderType === 'system'
                          ? { background: 'var(--c-primary-card)', border: '0.5px solid var(--c-border)' }
                          : { background: '#3b82f6' }
                        }
                      >
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                      </motion.div>
                      <div className="flex items-center gap-2 mt-1.5 px-2">
                        <span className="text-xs text-label font-medium">
                          {new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {msg.isRead && msg.senderType === 'user' && (
                          <CheckCheck className="text-accent-green" size={14} />
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Zone de saisie */}
            <div className="px-6 py-3" style={{ borderTop: '0.5px solid var(--c-border)', background: 'var(--c-primary-card)' }}>
              {!aiEnabled && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 shadow-lg"
                >
                  <Power className="text-red-500" size={14} />
                  <p className="text-xs text-red-400 font-medium">L'agent IA est désactivé pour cette conversation</p>
                </motion.div>
              )}
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Écrivez un message..."
                    disabled={sending}
                    className="w-full text-ink px-5 py-3.5 pr-12 rounded-xl outline-none focus:ring-1 focus:ring-accent-green/40 transition-all disabled:opacity-50"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.08, rotate: -5 }}
                  whileTap={{ scale: 0.92 }}
                  onClick={handleSendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="bg-accent-green text-black p-3.5 rounded-xl hover:opacity-90 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                  ) : (
                    <Send size={20} />
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--c-primary-dark)' }}>
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-text-gray mx-auto mb-4" />
              <p className="text-text-gray">Sélectionnez une conversation</p>
            </div>
          </div>
        )}
      </div>
  );
}
